// src/lib/notificationDomain.js
// #314 and #315, Session 79.12 — ONE ANSWER TO "WHICH MODULE IS THIS BELL FROM".
//
// ── WHAT WAS WRONG, in plain words ──
//
// A notification about an invoice showed up in three places wearing three
// different faces. The desktop bell painted it green and badged it INVENTORY.
// The mobile sheet painted it grey with no badge at all. The full Notifications
// page filtered it in the database and could not find it under any chip.
//
// Not three bugs — ONE cause, written down three times:
//
//   Topbar.js            read EVENT_TYPE_DOMAIN[event_type]
//   NotificationOverlay  read the `domain` COLUMN
//   NotificationsPage    filtered the `domain` COLUMN, server-side
//
// So the two surfaces that read the column agreed with each other and disagreed
// with the bell, and nothing could reconcile them — which is exactly why #314
// says its remaining half "needs this surface to read the domain column for the
// first time, which is a structural change, not a palette addition."
//
// THE COLUMN IS THE TRUTH AND THE MAP IS A FALLBACK. Every notification written
// since Session 62b carries `domain`; the map survives for rows older than that
// and for any writer that still omits it. A row with neither is grey and
// unbadged, which is honest rather than a guess.
//
// ── DECISION 3, 74z4 — ONE SHARED CHIP, TWO COLOURS ──
//
// Invoicing is its own notification family (#284's decision 1) and is NOT its
// own chip. An executive should read which END of purchasing fired without
// reading a word: ORANGE for the start — somebody ordered something — and
// MAGENTA for the close — the money side. Under one chip, because splitting
// them would make a reader check two places for one story.
//
// That is why `domainOf` and `chipOf` are different questions and why the
// server-side filter needs `domainsForChip()` rather than an equality test: the
// Purchasing chip covers TWO domain values in the database.
//
// ── THE MAGENTA ──
//
// `#B31F7C`, and it is not invented here. CLAUDE.md's gate palette already
// names it "Invoicing magenta", and FIVE approved gates use it as their accent —
// #299, #395, #343, #298 and 78v's email gate. What it is NOT yet is a member
// of CLAUDE.md's "Domain colors (locked)" list, which names four. That list is
// Colby's to change and the omission is flagged in #314 rather than assumed
// away.

const MAGENTA = '#B31F7C';

export const DOMAIN_COLORS = {
  inventory:   '#1D9E75',
  tools:       '#378ADD',
  purchasing:  '#EF9F27',
  safety:      '#E24B4A',
  suggestions: '#8B5FBF',
  invoicing:   MAGENTA,
  general:     '#888780',
};

export const DOMAIN_LABELS = {
  inventory:   'Inventory',
  tools:       'Tools',
  purchasing:  'Purchasing',
  safety:      'Safety',
  suggestions: 'Suggestions',
  invoicing:   'Invoicing',
  general:     'General',
};

// The rail and badge when a row names no domain we recognise. Grey says
// "unclassified" rather than picking a module at random.
export const UNKNOWN_COLOR = '#9A9A9A';

// Decision 3. A domain that rides another domain's chip goes here; everything
// else is its own chip. Deliberately a map rather than a condition, so a second
// case is data instead of a branch.
export const CHIP_OF_DOMAIN = {
  invoicing: 'purchasing',
};

// MOVED VERBATIM OUT OF Topbar.js at 79.12, not retyped. 79.9 typed a list of
// 38 values from memory and the database refused it; the rule that came out of
// that is to derive anything the system already holds rather than reproduce it.
// 35 entries, extracted programmatically and asserted at both ends.
//
// IT IS THE FALLBACK, NOT THE SOURCE. Rows written since Session 62b carry the
// `domain` column and never reach this map.
export const EVENT_TYPE_DOMAIN = {
  restock: 'inventory',
  restock_request: 'inventory',
  off_catalog_request: 'inventory',
  audit: 'inventory',
  tool_tagged_out: 'tools',
  order_shipped: 'inventory',
  receipt_confirmed: 'tools',
  spare_request: 'tools',
  inspection_discrepancy: 'tools',
  request_approved: 'tools',
  spare_request_created: 'tools',
  restock_needed: 'tools',
  request_rejected: 'tools',
  pending_tool: 'tools',
  print_job_done: 'inventory',
  print_job_error: 'inventory',
  inspection_overdue: 'safety',
  suggestion: 'suggestions',
  material_request_approved: 'inventory',
  material_request_denied: 'inventory',
  // #395, Session 78n8.2 -- purchasing placed the order with a vendor. Sits with
  // its two siblings above because it is the same lifecycle seen by the same
  // person: the requester, following their own material request.
  material_request_ordered: 'inventory',
  // #413, Session 78n8.3 -- the same moment as the line above, seen by the other
  // reader. material_request_ordered goes to the person who ASKED for the
  // material; this goes to the managers who APPROVED the spend, and it opens the
  // purchase order rather than their own request.
  //
  // 'inventory' rather than 'purchasing' even though a buyer triggered it, and
  // the rule is that the DOMAIN FOLLOWS THE READER'S DESTINATION, not the actor:
  // it lands on the Requests inbox, which moduleForPage() resolves to inventory.
  // Colouring it purchasing orange would give it the accent of a module these
  // recipients cannot open a single page of.
  material_request_po_issued: 'inventory',
  purchase_approval_needed: 'purchasing',
  purchase_approval_pending: 'purchasing',
  purchase_approval_denied: 'purchasing',
  purchase_approval_granted: 'purchasing',
  // #306. This map is keyed on event_type, NOT on the notifications.domain column --
  // this surface has never read that column (NOTIFICATION_VOCABULARY.md Finding 6).
  // So a new value is invisible to it even when its domain already has a chip, and
  // getDomainKey() returning null costs four things at once: the card and toast rails
  // fall back to grey instead of Purchasing orange, the Purchasing chip's unread count
  // excludes it, and filtering to Purchasing HIDES it entirely -- which is worse than
  // no chip, because Jennifer's own domain filter is where she would look for it.
  // NotificationOverlay.js and NotificationsPage.js both read the domain column and
  // need no change; this desktop map is the only surface that does.
  purchase_ready_to_order: 'purchasing',
  // Session 74z5, absorbed while this file was open. #301's two invoicing values
  // carry domain 'invoicing' and were in no map at all, so they rendered with a grey
  // rail and NO chip -- observed live on staging, not predicted: the bell read
  // INVENTORY 21 + PURCHASING 7 against ALL 30, and the missing 2 were these.
  // Mapped to 'purchasing' because that IS decision 3 at 74z4: Invoicing rides
  // Purchasing's orange chip. The other half of that decision -- magenta CARDS under
  // the orange chip -- is deliberately NOT done here: it needs this surface to read
  // the notifications.domain column, which it has never done (Finding 6), and that
  // is the Sep 7 chrome pass. Chip now correct, card colour still shared.
  invoice_discrepancy_resolved: 'purchasing',
  invoice_awaiting_entry: 'purchasing',
  // #371 and #425, Session 79.6, gate decision D12. Inter-branch transfers, both
  // fulfilment modes -- moving stock off a shelf and making it to order.
  //
  // 'inventory' for all six, and by the rule the comment above states: THE DOMAIN
  // FOLLOWS THE READER'S DESTINATION. A transfer is stock movement, not a
  // purchase -- there is no vendor, no purchase order and no invoice, which is
  // the whole of #425 -- and every one of these lands on an inventory screen.
  //
  // THIS BLOCK IS THE PART THAT GETS SKIPPED, and the gate says so outright.
  // This map is keyed on event_type and never reads the notifications.domain
  // column, so writing domain:'inventory' at the call site buys NOTHING here:
  // getDomainKey() returns null, the rail goes grey, the Inventory chip's unread
  // count excludes it, and filtering to Inventory HIDES IT ENTIRELY -- in the one
  // place its recipient would look. #306 was recorded as needing no chrome work
  // for exactly this reason and that was wrong.
  transfer_requested: 'inventory',
  transfer_approved: 'inventory',
  transfer_denied: 'inventory',
  transfer_shipped: 'inventory',
  transfer_received: 'inventory',
  transfer_closed_short: 'inventory',
  // #590 and #592, Session 79.21b -- four new endings, one chip each (Finding 5:
  // one type, one chip). A withdrawn transfer and a withdrawn or canceled
  // purchase request land on inventory screens; a purchase request withdrawn
  // while it waited on Purchasing lands on the Purchasing Queue.
  transfer_withdrawn: 'inventory',
  material_request_withdrawn: 'inventory',
  purchase_request_withdrawn: 'purchasing',
  material_request_cancelled: 'inventory',
  // #614, Session 79.22b (gate 371 D40) -- Purchasing sent a request to one of
  // our own branches as a transfer. It lands on the transfer, an inventory
  // screen, like the other request endings above.
  material_request_sent_as_transfer: 'inventory',
  // #682 / #702 / #761, Session 79.22b.6c.3 (the corrections gate's B5) -- the
  // five correction bells. Every one lands on an inventory screen: a transfer,
  // Awaiting confirmation, or the item's page.
  transfer_corrected: 'inventory',
  transfer_step_corrected: 'inventory',
  production_run_corrected: 'inventory',
  transfer_extra_counted: 'inventory',
  transfer_extra_answered: 'inventory',
};

// ── THE FOUR QUESTIONS ────────────────────────────────────────────────────────
//
// Each takes the NOTIFICATION, never an event type. That signature is the fix:
// a function given only `event_type` cannot read the column, and every caller
// that passed one was structurally unable to be correct.

export function domainOf(n) {
  if (!n) return null;
  if (n.domain && DOMAIN_COLORS[n.domain]) return n.domain;
  return EVENT_TYPE_DOMAIN[n.event_type] || null;
}

// Which chip this row sits under — usually its own domain, except where
// decision 3 folds one into another.
export function chipOf(n) {
  const d = domainOf(n);
  if (!d) return null;
  return CHIP_OF_DOMAIN[d] || d;
}

export function colorOf(n) {
  const d = domainOf(n);
  return d ? (DOMAIN_COLORS[d] || UNKNOWN_COLOR) : UNKNOWN_COLOR;
}

export function labelOf(n) {
  const d = domainOf(n);
  return d ? (DOMAIN_LABELS[d] || d) : null;
}

// THE INVERSE, and it exists because NotificationsPage filters in the DATABASE.
// A chip covering two domain values cannot be an equality test there — it is
// `.in('domain', domainsForChip(chip))`. Getting this wrong does not throw; it
// silently returns a list with the invoicing rows missing, which is the shape
// #314 was logged for in the first place.
export function domainsForChip(chip) {
  if (!chip || chip === 'all') return null;
  const folded = Object.keys(CHIP_OF_DOMAIN).filter(d => CHIP_OF_DOMAIN[d] === chip);
  return [chip, ...folded];
}

// The chip row, in the order the modules are read. `suggestions` is appended by
// the caller when the reader may open Speak Up — it is the one chip gated on a
// permission, so it is not baked in here.
export const BASE_CHIPS = ['all', 'inventory', 'tools', 'purchasing', 'safety'];

export function chipsFor({ canSeeSuggestions } = {}) {
  return canSeeSuggestions ? [...BASE_CHIPS, 'suggestions'] : [...BASE_CHIPS];
}
